import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { environment } from '../../../../environment/environment';
import { SearchMessageDto } from './model/search-message-dto';
import { map, Observable, Subject, Subscription } from 'rxjs';
import { UserService } from '../../../../common/service/user.service';
import { AuthService } from '../../../auth/auth.service';
import { TranslationService } from '../../../../common/service/translate.service';
import { SearchDocumentItem } from './model/search-document-item';
import { AiFeedback } from './model/ai-feedback-dto';

@Injectable({
  providedIn: 'root',
})
export class SearchService {
  // Base URL for the document service endpoint, configured in the environment file
  private serviceEndpoint = environment.APIBase;
  // Inject HttpClient to handle HTTP requests
  private translationService = inject(TranslationService);
  private httpClient = inject(HttpClient);
  constructor(
    private userService: UserService,
    private authService: AuthService,
  ) {}

  public submitFeedback(feedback: AiFeedback): Promise<string> {
    return new Promise((resolve, reject) => {
      this.httpClient.post<string>(`${this.serviceEndpoint}Ai/feedback`, feedback).subscribe(
        (res: string) => {
          resolve(res);
        },
        (error: any) => {
          console.error('Error in submitFeedback', error);
          reject(error);
        },
      );
    });
  }

  public chatWithAI(SearchMessageDto: SearchMessageDto, abortSignal: AbortSignal) {
    return new Observable((observer) => {
      this.authService.getAccessToken().then((res) => {
        const accessToken = res;
        const headers: Record<string, string> = {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
          lang: this.translationService?.getCurrentLanguage() ?? 'en-US',
        };

        if (this.userService.isImpersonating()) {
          const impersonationContext = this.userService.getImpersonationContext();
          if (impersonationContext) {
            headers['ImpersonatingUser'] = impersonationContext.impersonatedUserSubject != null ? impersonationContext.impersonatedUserSubject + '' : '';
            headers['customerId'] = impersonationContext.customerId + '';
          }
        }
        const abortSubject = new Subject<void>();
        // Listen for the abort event and complete the Subject
        abortSignal.addEventListener('abort', () => {
          abortSubject.next();
          abortSubject.complete();
        });

        fetch(`${this.serviceEndpoint}Ai/chat`, {
          method: 'POST',
          headers: headers,
          body: JSON.stringify(SearchMessageDto),
          signal: abortSignal,
        })
          .then((response) => {
            const reader = response.body?.getReader();
            if (!reader) {
              observer.error('No stream reader available');
              return;
            }

            const decoder = new TextDecoder();

            const readStream = () => {
              reader
                .read()
                .then(({ done, value }) => {
                  if (done) {
                    observer.complete();
                    return;
                  }

                  observer.next(decoder.decode(value)); // Send streamed data
                  readStream();
                })
                .catch((error) => observer.error(error));
            };

            readStream();
          })
          .catch((error) => {
            if (abortSignal.aborted) {
              console.log('Search request aborted.');
            } else {
              observer.error(error);
              console.error('Error in searchByAi', error);
            }
          });
      });
    });
  }

  private searchSub?: Subscription;

  public searchDocument(documentNumber: string): Promise<SearchDocumentItem> {
    // Cancel previous request if running
    if (this.searchSub) {
      this.searchSub.unsubscribe();
    }

    return new Promise<SearchDocumentItem>((resolve, reject) => {
      this.searchSub = this.httpClient
        .get<SearchDocumentItem>(`${this.serviceEndpoint}Document/allCustomer?documentNumber=${documentNumber}`)
        .pipe(map((res) => res as SearchDocumentItem))
        .subscribe(
          (res: SearchDocumentItem) => {
            resolve(res);
          },
          (error: any) => {
            console.error('Error in getSearchDocument', error);
            reject(error);
          },
        );
    });
  }
}
